import { Link } from "react-router-dom";
import { Pct } from "@/components/layernav/primitives";
import { Droplets, ArrowUpRight } from "lucide-react";
import { fmtBig } from "./SectorHero";
import { canonicalProtocolSlug, entityNavRoute, entityTrack, resolveEntity, useEntityRegistry } from "@/lib/entities";

const CARD = "rounded-xl border border-white/[0.08] bg-[#0b0c10] p-4 sm:p-5";
const title = (s: string) => s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const LiquidityIntelligence = ({ data }: { data: any }) => {
  const { data: reg } = useEntityRegistry();
  if (!data) return null;
  const stables = data.stablecoins || {};
  const pools: any[] = data.yields?.pools || [];
  const hasAny = data.dex_volume_24h_usd || stables.total_usd || pools.length;
  if (!hasAny) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10" data-testid="defi-liquidity-intel">
      <h2 className="font-heading font-bold text-white text-lg flex items-center gap-2.5 mb-4">
        <Droplets aria-hidden className="h-[18px] w-[18px] text-[#34D399]" />
        Liquidity Intelligence
      </h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className={CARD} data-testid="liquidity-dex-volume">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#00E5FF]">DEX Volume · 24H</p>
          <div className="flex items-baseline gap-2.5 mt-3">
            <span className="font-mono text-3xl font-bold text-white">{fmtBig(data.dex_volume_24h_usd)}</span>
            {data.dex_volume_change_24h_pct != null && <Pct v={data.dex_volume_change_24h_pct} className="!text-[12px]" />}
          </div>
          <p className="text-[12.5px] text-gray-400 leading-relaxed mt-2.5">
            Aggregated spot volume across all tracked decentralized exchanges.
          </p>
          <p className="font-mono text-[8.5px] uppercase tracking-wider text-gray-500 mt-3">Source · DeFiLlama</p>
        </div>
        <div className={CARD} data-testid="liquidity-stablecoins">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#A78BFA]">Stablecoin Composition</p>
          <div className="flex items-baseline gap-2.5 mt-3">
            <span className="font-mono text-2xl font-bold text-white">{fmtBig(stables.total_usd)}</span>
            {stables.change_24h_pct != null && <Pct v={stables.change_24h_pct} className="!text-[11px]" />}
          </div>
          <div className="mt-3 space-y-2">
            {(stables.top || []).slice(0, 4).map((s: any) => (
              <div key={s.symbol} data-testid={`stable-share-${s.symbol}`}>
                <div className="flex items-center justify-between font-mono text-[10.5px]">
                  <span className="text-gray-300 font-bold">{s.symbol}</span>
                  <span className="text-gray-400">{fmtBig(s.circulating_usd)} · {s.share_pct}%</span>
                </div>
                <div className="mt-1 h-1 rounded-full bg-white/[0.06] overflow-hidden">
                  <div className="h-full rounded-full bg-[#34D399]/70" style={{ width: `${Math.min(s.share_pct, 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={`${CARD} sm:col-span-2 lg:col-span-1`} data-testid="liquidity-yields">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#34D399]">Top Yield Pools</p>
          <div className="mt-3 space-y-2.5">
            {pools.slice(0, 5).map((p, i) => {
              const cslug = canonicalProtocolSlug(reg, p.project);
              const route = cslug ? entityNavRoute(resolveEntity(reg, "protocol", cslug)) : null;
              return (
              <div key={`${p.project}-${p.symbol}-${i}`} className="flex items-center justify-between gap-3"
                data-testid={`yield-pool-${p.project}-${i}`} data-live={route ? "true" : "false"}>
                <span className="min-w-0">
                  <span className="block text-[12.5px] font-semibold text-gray-200 leading-tight truncate">
                    {route ? (
                      <Link to={route} aria-label={`${title(p.project || "")} — Entity Intelligence`}
                        data-testid={`entity-link-protocol-${cslug}-pool-${i}`}
                        onClick={() => entityTrack("entity_nav_click", `protocol:${cslug}`, { surface: "yield_pools" })}
                        className="group inline-flex items-center gap-0.5 rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#00E5FF]/60">
                        <span className="transition-colors duration-200 group-hover:text-[#00E5FF]">{title(p.project || "")}</span>
                        <ArrowUpRight aria-hidden
                          className="h-2.5 w-2.5 shrink-0 text-[#00E5FF] opacity-50 md:opacity-0 transition-opacity duration-200 md:group-hover:opacity-100 md:group-focus-visible:opacity-100" />
                      </Link>
                    ) : title(p.project || "")}
                    {" "}· <span className="font-mono text-[11px] text-gray-400">{p.symbol}</span>
                  </span>
                  <span className="block font-mono text-[9px] uppercase tracking-wide text-gray-500">{p.chain} · {fmtBig(p.tvl_usd)} TVL</span>
                </span>
                <span className="font-mono text-[13px] font-bold text-[#34D399] whitespace-nowrap">{p.apy_pct}% APY</span>
              </div>
              );
            })}
          </div>
          <p className="font-mono text-[8.5px] uppercase tracking-wider text-gray-500 mt-3" title={data.yields?.rule}>
            Largest eligible pools by TVL · DeFiLlama
          </p>
        </div>
      </div>
    </section>
  );
};
